import type {ReactElement} from 'react';
import type {AnimationLightboxHeaderContentProps, CollapsibleHeaderAction} from './animation-lightbox-header-content.types';
import styles from './animation-lightbox-header-content.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, Size, Type} from '@Components/button';
import {getUrlForAiChatAnimation} from '@Libraries/ai-chat.library';
import {downloadTrimmedAiChatAnimation} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import {downloadBlobAsFile} from '@Utils/file.util';
import {useOpenChatAnimationInEditor} from '@Components/ai-chat-window/hooks/useOpenChatAnimationInEditor';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7/controlled-list-item-type-7';
import {PublishingOptions} from '@Components/ai-chat-window/components/publishing-options/publishing-options';
import {LoadingStates} from '@Utils/loading.util';
import {useAiChat} from '@AiChat/providers/ai-chat.context';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {usePublishChatAnimation} from '@Components/ai-chat-window/hooks/usePublishChatAnimation';
import {useResetAnimationActionLoadingStatesOnModalClose} from '@Components/ai-chat-window/hooks/useResetAnimationActionLoadingStatesOnModalClose';
import {AiChatVideoActionSource, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';

export function AnimationLightboxHeaderContent({onClose, chatId, messageId, animationHashedId, isWriteActionEnabled, isCreating, startTime, endTime, duration, isMuted}: AnimationLightboxHeaderContentProps): ReactElement {
  const {state, actions} = useAiChat();
  const {isSmall} = useAiChatSize();
  const videoActionSource = state.isAssetsView ? AiChatVideoActionSource.MY_DESIGNS : AiChatVideoActionSource.CHAT;
  const {openAnimationInEditor, isLoading} = useOpenChatAnimationInEditor({chatId, animationHashedId, source: videoActionSource});
  const {publishToSocialMedia, promoteAsEvent, publishAsEmailCampaign} = usePublishChatAnimation(chatId, animationHashedId, videoActionSource);

  useResetAnimationActionLoadingStatesOnModalClose(chatId, animationHashedId);

  const loadingStates = state.getLoadingStatesForAnimationActions(chatId, animationHashedId);
  const isPublishingLoading = loadingStates.publishingLoadingState === LoadingStates.LOADING;
  const isDownloadLoading = loadingStates.downloadLoadingState === LoadingStates.LOADING;
  const isDisabled = isLoading || isPublishingLoading || isDownloadLoading || isCreating;

  const isTrimmed = startTime !== undefined && endTime !== undefined && duration !== undefined && (startTime > 0 || endTime < duration);

  const handleDownload = async (): Promise<void> => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_TURNTOVIDEO_DOWNLOAD, {[GA4EventParamName.SOURCE]: videoActionSource});
    actions.updateLoadingStatesForAnimationActions(chatId, animationHashedId, {
      downloadLoadingState: LoadingStates.LOADING,
    });
    try {
      if (isTrimmed || isMuted) {
        await downloadTrimmedAiChatAnimation(animationHashedId, isMuted);
      } else {
        const res = await fetch(getUrlForAiChatAnimation(animationHashedId));
        const blob = await res.blob();
        downloadBlobAsFile(blob, `PosterMyWall_GeneratedVideo_${animationHashedId.substring(0, 8)}.mp4`);
      }
    } finally {
      actions.updateLoadingStatesForAnimationActions(chatId, animationHashedId, {
        downloadLoadingState: LoadingStates.NOT_LOADED,
      });
    }
  };

  const handleOpenChat = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_MY_AI_DESIGN_OPEN_IN_CHAT);
    actions.setCurrentChatId(chatId, {messageId});
  };

  const getCollapsibleActions = (): CollapsibleHeaderAction[] => {
    const headerActions: CollapsibleHeaderAction[] = [
      {
        id: 'edit-video-in-editor',
        text: window.i18next.t('pmwjs_edit_video_in_editor'),
        icon: 'icon-pencil',
        isDisabled: !isWriteActionEnabled || isDisabled,
        isLoading,
        onClick: (): void => {
          void openAnimationInEditor();
        },
      },
    ];

    if (state.isAssetsView && messageId) {
      headerActions.push({
        id: 'open-chat',
        text: window.i18next.t('pmwjs_open_chat'),
        icon: 'icon-external-link',
        isDisabled: false,
        onClick: handleOpenChat,
      });
    }

    return headerActions;
  };

  const getCollapsibleActionsMarkup = (): ReactElement => {
    const headerActions = getCollapsibleActions();

    if (!isSmall) {
      return getActionButtons(headerActions);
    }

    if (headerActions.length === 1) {
      return getSingleActionIcon(headerActions[0]);
    }

    return getMoreOptionsDropdown(headerActions);
  };

  const getActionButtons = (headerActions: CollapsibleHeaderAction[]): ReactElement => {
    return (
      <>
        {headerActions.map((action) => (
          <Button
            key={action.id}
            text={action.text}
            size={Size.XSMALL}
            type={Type.DARK_GHOST}
            icon={action.icon}
            isLoading={action.isLoading}
            disabled={action.isDisabled}
            customClasses={styles.headerButton}
            onClick={action.onClick}
          />
        ))}
      </>
    );
  };

  const getSingleActionIcon = (action: CollapsibleHeaderAction): ReactElement => {
    return (
      <Icon
        icon={action.icon}
        size={IconSize.SIZE_ICON_16}
        type={IconType.DARK_GHOST}
        shape={IconShape.SQUARE}
        ariaLabel={action.text}
        onClick={action.onClick}
        isDisabled={action.isDisabled}
        isLoading={action.isLoading}
      />
    );
  };

  const getMoreOptionsDropdown = (headerActions: CollapsibleHeaderAction[]): ReactElement => {
    return (
      <Dropdown
        showModalForSmallScreen
        modalTitle={window.i18next.t('pmwjs_more_options')}
        popupClassName={styles.actionPopup}
        className={styles.positionUnset}
        doCloseOnInnerClick
        selector={<Icon icon="icon-more-h" size={IconSize.SIZE_ICON_16} type={IconType.DARK_SECONDARY} shape={IconShape.SQUARE} isDisabled={isDisabled} />}
        popup={
          <>
            {headerActions.map((action) => (
              <ControlledListItemType7
                key={action.id}
                id={action.id}
                text={action.text}
                icon={action.icon}
                onClick={action.onClick}
                isDisabled={action.isDisabled}
                isLoading={action.isLoading}
                className="radius-8"
              />
            ))}
          </>
        }
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
      />
    );
  };

  const getDownloadAction = (): ReactElement => {
    if (isSmall) {
      return (
        <Icon
          icon={'icon-download'}
          size={IconSize.SIZE_ICON_16}
          type={IconType.DARK_SECONDARY}
          shape={IconShape.SQUARE}
          onClick={(): void => {
            void handleDownload();
          }}
          isDisabled={isDisabled}
          isLoading={isDownloadLoading}
        />
      );
    }

    return (
      <Button
        text={window.i18next.t('pmwjs_download')}
        size={Size.XSMALL}
        type={Type.DARK_SECONDARY}
        icon={'icon-download'}
        isLoading={isDownloadLoading}
        disabled={isDisabled}
        customClasses={styles.headerButton}
        onClick={(): void => {
          void handleDownload();
        }}
      />
    );
  };

  const getPublishDropdown = (): ReactElement => {
    return (
      <Dropdown
        showModalForSmallScreen
        modalTitle={window.i18next.t('pmwjs_publish')}
        popupClassName={styles.actionPopup}
        className={styles.positionUnset}
        selector={
          <Button
            text={window.i18next.t('pmwjs_publish')}
            size={Size.XSMALL}
            type={Type.SECONDARY}
            icon={'icon-publish-arrow'}
            isLoading={isPublishingLoading}
            disabled={isDisabled || !isWriteActionEnabled}
            customClasses={styles.headerButton}
          />
        }
        popup={
          <PublishingOptions
            chatId={chatId}
            hashedId={animationHashedId}
            loadingStates={loadingStates}
            publishToSocialMedia={publishToSocialMedia}
            publishAsEmailCampaign={publishAsEmailCampaign}
            promoteAsEvent={promoteAsEvent}
          />
        }
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
      />
    );
  };

  return (
    <div className={styles.lightboxHeader}>
      <Icon
        icon={'icon-arrow-left'}
        size={IconSize.SIZE_ICON_16}
        type={IconType.DARK_GHOST}
        ariaLabel={window.i18next.t('pmwjs_back')}
        shape={IconShape.SQUARE}
        onClick={onClose}
      />
      <div className={styles.headerActions}>
        {getCollapsibleActionsMarkup()}
        {getDownloadAction()}
        {getPublishDropdown()}
      </div>
    </div>
  );
}
